/**
 * App — Main application component
 *
 * Composes the react-leaflet MapContainer with all layer and UI components.
 * Centered on St. Louis County, MN.
 */

import React, { useState, useCallback } from 'react'
import { MapContainer, TileLayer, useMapEvents, useMap } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'

import {
  USGS_TOPO_URL,
  ESRI_SATELLITE_URL,
  LAYER_OPTIONS,
} from './layers/topoLayer.js'
import { CountyBoundaries } from './layers/countyBounds.jsx'
import { MgrsGridOverlay } from './layers/mgrsGrid.js'
import CoordinatePanel from './components/CoordinatePanel.jsx'
import SearchBar from './components/SearchBar.jsx'
import LayerControl from './components/LayerControl.jsx'
import ScaleDisplay from './components/ScaleDisplay.jsx'
import CopyToast from './components/CopyToast.jsx'
import PrintDialog from './components/PrintDialog.jsx'
import DeclinationDisplay from './components/DeclinationDisplay.jsx'
import WaypointManager from './components/WaypointManager.jsx'
import MeasureTool from './components/MeasureTool.jsx'
import ToolBar from './components/ToolBar.jsx'
import ElevationProfile from './components/ElevationProfile.jsx'
import CacheControl from './components/CacheControl.jsx'
import { latLngToMgrs, formatMgrsDisplay } from './utils/mgrsConvert.js'

/** Default map center: St. Louis County, MN */
const DEFAULT_CENTER = [47.5, -92.5]
const DEFAULT_ZOOM = 8

/**
 * Internal component that listens to map events and feeds cursor
 * position + zoom level back to the parent via callbacks.
 */
function MapEventHandler({ onMouseMove, onMouseOut, onZoomEnd, onMapClick, onMoveEnd }) {
  useMapEvents({
    mousemove(e) {
      onMouseMove(e.latlng)
    },
    mouseout() {
      onMouseOut()
    },
    zoomend(e) {
      onZoomEnd(e.target.getZoom())
      if (onMoveEnd) {
        const b = e.target.getBounds()
        onMoveEnd({
          n: b.getNorth(),
          s: b.getSouth(),
          e: b.getEast(),
          w: b.getWest(),
        })
      }
    },
    moveend(e) {
      if (onMoveEnd) {
        const b = e.target.getBounds()
        onMoveEnd({
          n: b.getNorth(),
          s: b.getSouth(),
          e: b.getEast(),
          w: b.getWest(),
        })
      }
    },
    click(e) {
      onMapClick(e.latlng)
    },
  })
  return null
}

/**
 * Internal component that exposes the map instance for flyTo operations.
 */
function FlyToHandler({ target }) {
  const map = useMap()

  React.useEffect(() => {
    if (target) {
      map.flyTo([target.lat, target.lng], 13, { duration: 1.5 })
    }
  }, [target, map])

  return null
}

function App() {
  // --- Layer visibility state ---
  const [layers, setLayers] = useState({
    topo: true,
    satellite: false,
    counties: true,
    mgrsGrid: true,
  })

  // --- Cursor coordinate state ---
  const [cursorCoords, setCursorCoords] = useState({ lat: null, lng: null })

  // --- Current zoom level ---
  const [zoom, setZoom] = useState(DEFAULT_ZOOM)

  // --- Fly-to target from search ---
  const [flyTarget, setFlyTarget] = useState(null)

  // --- Click-to-copy toast ---
  const [copyToast, setCopyToast] = useState(null) // { lat, lng, mgrs }

  // --- Print dialog visibility ---
  const [showPrint, setShowPrint] = useState(false)

  // --- Map bounds for cache control ---
  const [mapBounds, setMapBounds] = useState(null)

  // --- Active tool (waypoint | measure | elevation | null) ---
  const [activeTool, setActiveTool] = useState(null)

  // --- Elevation profile points ---
  const [elevationPoints, setElevationPoints] = useState([])

  // --- Waypoints ---
  const [waypoints, setWaypoints] = useState([])

  const handleToggle = useCallback((key) => {
    setLayers((prev) => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const handleMouseMove = useCallback((latlng) => {
    setCursorCoords({ lat: latlng.lat, lng: latlng.lng })
  }, [])

  const handleMouseOut = useCallback(() => {
    setCursorCoords({ lat: null, lng: null })
  }, [])

  const handleZoomEnd = useCallback((z) => {
    setZoom(z)
  }, [])

  const handleMoveEnd = useCallback((bounds) => {
    setMapBounds(bounds)
  }, [])

  const handleMapClick = useCallback((latlng) => {
    // When waypoint tool is active, add a waypoint instead of copy-to-clipboard
    if (activeTool === 'waypoint') {
      try {
        const raw = latLngToMgrs(latlng.lat, latlng.lng, 3)
        const mgrs = formatMgrsDisplay(raw)
        setWaypoints((prev) => [
          ...prev,
          { id: Date.now(), lat: latlng.lat, lng: latlng.lng, label: `WP${prev.length + 1}`, mgrs },
        ])
      } catch { /* ignore */ }
      return
    }
    // When measure tool is active, let MeasureTool handle clicks
    if (activeTool === 'measure') return

    // When elevation tool is active, collect two points for the profile
    if (activeTool === 'elevation') {
      setElevationPoints((prev) => {
        if (prev.length < 2) {
          return [...prev, { lat: latlng.lat, lng: latlng.lng }]
        }
        // Reset and start from the new first point
        return [{ lat: latlng.lat, lng: latlng.lng }]
      })
      return
    }

    try {
      const raw = latLngToMgrs(latlng.lat, latlng.lng, 3)
      const formatted = formatMgrsDisplay(raw)
      navigator.clipboard.writeText(formatted).catch(() => {})
      setCopyToast({ lat: latlng.lat, lng: latlng.lng, mgrs: formatted })
    } catch { /* ignore */ }
  }, [activeTool])

  const handleAddWaypoint = useCallback((wp) => {
    setWaypoints((prev) => [...prev, wp])
  }, [])

  const handleRemoveWaypoint = useCallback((id) => {
    setWaypoints((prev) => prev.filter((w) => w.id !== id))
  }, [])

  const handleUpdateWaypoint = useCallback((id, updates) => {
    setWaypoints((prev) =>
      prev.map((w) => (w.id === id ? { ...w, ...updates } : w))
    )
  }, [])

  const handleDismissToast = useCallback(() => {
    setCopyToast(null)
  }, [])

  const handleToolChange = useCallback((tool) => {
    setActiveTool(tool)
    // Clear elevation points when switching away from elevation tool
    if (tool !== 'elevation') {
      setElevationPoints([])
    }
  }, [])

  const handleElevationClose = useCallback(() => {
    setElevationPoints([])
  }, [])

  const handleSearch = useCallback((coords) => {
    setFlyTarget({ ...coords, _ts: Date.now() })
  }, [])

  return (
    <div id="map-container">
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        style={{ width: '100%', height: '100%' }}
        zoomControl={true}
      >
        {/* Map event listeners */}
        <MapEventHandler
          onMouseMove={handleMouseMove}
          onMouseOut={handleMouseOut}
          onZoomEnd={handleZoomEnd}
          onMapClick={handleMapClick}
          onMoveEnd={handleMoveEnd}
        />

        {/* Fly-to on search */}
        <FlyToHandler target={flyTarget} />

        {/* Base tile layers */}
        {layers.topo && (
          <TileLayer url={USGS_TOPO_URL} {...LAYER_OPTIONS.topo} />
        )}
        {layers.satellite && (
          <TileLayer url={ESRI_SATELLITE_URL} {...LAYER_OPTIONS.satellite} />
        )}

        {/* County boundaries */}
        <CountyBoundaries visible={layers.counties} />

        {/* MGRS Grid Overlay */}
        <MgrsGridOverlay visible={layers.mgrsGrid} />

        {/* Waypoint markers */}
        <WaypointManager
          waypoints={waypoints}
          onAdd={handleAddWaypoint}
          onRemove={handleRemoveWaypoint}
          onUpdate={handleUpdateWaypoint}
          addMode={activeTool === 'waypoint'}
        />

        {/* Distance/bearing measurement */}
        <MeasureTool active={activeTool === 'measure'} />
      </MapContainer>

      {/* UI Overlays */}
      <CoordinatePanel lat={cursorCoords.lat} lng={cursorCoords.lng} />
      <SearchBar onSearch={handleSearch} />
      <LayerControl layers={layers} onToggle={handleToggle} />
      <ScaleDisplay zoom={zoom} />
      <DeclinationDisplay
        lat={cursorCoords.lat ?? DEFAULT_CENTER[0]}
        lng={cursorCoords.lng ?? DEFAULT_CENTER[1]}
      />
      <button
        className="print-btn"
        type="button"
        onClick={() => setShowPrint(true)}
      >
        Print Map
      </button>
      <PrintDialog
        open={showPrint}
        onClose={() => setShowPrint(false)}
        mapCenter={{
          lat: cursorCoords.lat ?? DEFAULT_CENTER[0],
          lng: cursorCoords.lng ?? DEFAULT_CENTER[1],
        }}
        mapBounds={null}
      />
      <ToolBar activeTool={activeTool} onToolChange={handleToolChange} />
      <CacheControl mapBounds={mapBounds} zoom={zoom} />
      {elevationPoints.length === 2 && (
        <ElevationProfile
          points={elevationPoints}
          onClose={handleElevationClose}
        />
      )}
      {copyToast && (
        <CopyToast mgrs={copyToast.mgrs} onDismiss={handleDismissToast} />
      )}
    </div>
  )
}

export default App
